/**
 * Chart of usage per year.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */

import { Kpi } from '@amplify3/duplo-back-end'
import type { ApexOptions } from 'apexcharts'
import { useEffect, useMemo, useRef, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import './UsageChart.css'

const FONT_SIZE = '13px'
const NUMBER_LOCALE = 'en-EN'
const BORDER_RADIUS = 5
const BAR_WIDTH = '35px'
const CHART_HEIGHT = '260px'
export const CHART_LABEL_MONTH_LIST = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const enum BarColor {
  MediumBlue = '#4A90D9',
  SelectedBlue = '#4ECDC4',
  Purple = '#7C6FCD',
  Red = '#F67D69',
  Teal = '#38B2AC',
}

export type UsageChartProps = {
  className?: string
  id?: string

  /**
   * The list of the KPI.
   */
  kpiList: Kpi[]

  selectedDate?: Date
  onBarClick?: (year: number, monthIndex: number) => void
}

export const UsageChart = (props: UsageChartProps) => {
  //  Use useMemo to avoid calling Kpi.getYearMonthFileCount at each rendering.
  const processedCountListPerYear: Record<number, number[]> = useMemo(
    () => Kpi.getYearMonthFileCount(props.kpiList),
    [props.kpiList]
  )

  const chartId = `usage-chart_${props.id}`
  const [selectedYear, setSelectedYear] = useState<number | null>()
  const [selectedBarIndex, setSelectedBarIndex] = useState<number | null>(
    props.selectedDate ? props.selectedDate.getMonth() : null
  )

  /**
   * Reference to the selected year.
   *
   * It is used to define the static CHART_OPTION. If selectedYear value is used
   * instead of the reference, CHART_OPTION will use the initial value which is
   * null. Reference must then be used instead.
   */
  const selectedYearRef = useRef(selectedYear)
  useEffect(() => {
    selectedYearRef.current = selectedYear
  }, [selectedYear])

  // Set initial value of selectedYear.
  useEffect(() => {
    if (!selectedYear) {
      const lastYear = Object.keys(processedCountListPerYear).at(-1)
      setSelectedYear(lastYear ? Number(lastYear) : null)
    }
  }, [processedCountListPerYear])

  useEffect(() => {
    if (props.selectedDate) {
      const date = new Date(props.selectedDate)
      setSelectedYear(date.getFullYear())
      setSelectedBarIndex(date.getMonth())
    }
  }, [props.selectedDate])

  /**
   * Memoized chart options: the object reference is kept stable
   * across re-renders so that react-apexcharts does not call
   * updateOptions() unnecessarily.
   *
   * Dependencies:
   * - `chartId`: options embed the chart ID, so a new ID requires
   *   a new options object.
   * - `selectedYear`: react-apexcharts triggers updateOptions() only
   *   when the options reference changes. Adding selectedYear here
   *   ensures a new reference is produced when the year changes,
   *   which forces ApexCharts to recalculate the y-axis scale for
   *   the new dataset. Without this, the scale stays locked to the
   *   first render.
   */
  const CHART_OPTION = useMemo(
    () =>
      ({
        chart: {
          id: chartId,
          animations: {
            enabled: true,
            dynamicAnimation: {
              enabled: true,
              speed: 150,
            },
          },
          toolbar: {
            tools: {
              download: false,
            },
          },
          events: {
            dataPointSelection: (_event, _chartContext, config) => {
              if (config && config.dataPointIndex >= 0) {
                setSelectedBarIndex(config.dataPointIndex)
                if (selectedYearRef.current && props.onBarClick) {
                  props.onBarClick(
                    selectedYearRef.current,
                    config.dataPointIndex
                  )
                }
              }
            },
          },
        },
        stroke: { width: 0 },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'left',
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: (val: number) =>
              Number(val).toLocaleString(NUMBER_LOCALE),
          },
        },
        states: {
          active: { filter: { type: 'none' } },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: CHART_LABEL_MONTH_LIST,
        },
        yaxis: {
          title: {
            text: 'Number of tracks',
            style: {
              color: '#666',
              fontSize: FONT_SIZE,
              fontWeight: 600,
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadiusApplication: 'end',
            borderRadiusWhenStacked: 'last',
            borderRadius: BORDER_RADIUS,
            columnWidth: BAR_WIDTH,
            dataLabels: {
              total: {
                enabled: true,
                offsetX: 0,
                style: {
                  fontSize: FONT_SIZE,
                  fontWeight: 900,
                },
                formatter(val) {
                  return Number(val).toLocaleString(NUMBER_LOCALE)
                },
              },
            },
          },
        },
      }) as ApexOptions,
    [chartId, selectedYear]
  )

  return (
    <ReactApexChart
      className={`usage-chart${props.className ? ` ${props.className}` : ''}`}
      type="bar"
      series={[
        {
          name: 'Track count',
          data: (selectedYear
            ? processedCountListPerYear[selectedYear]
            : new Array(12).fill(0)
          ).map((count: number, i: number) => ({
            x: CHART_LABEL_MONTH_LIST[i],
            y: count,
            fillColor:
              i === selectedBarIndex
                ? BarColor.SelectedBlue
                : BarColor.MediumBlue,
          })),
        },
      ]}
      options={CHART_OPTION}
      width="100%"
      height={CHART_HEIGHT}
    />
  )
}
